// Bring a fax row back to the middle of the screen after the inbox reloads.
//
// Acting on a fax reloads the whole list, which throws away your scroll
// position — so the entry you just matched is somewhere off screen and you have
// to hunt for it to see what happened to it.
//
// Rows are found by DOCUMENT key, not by path: matching MOVES the file
// (`Pending_Assignment/x.pdf` becomes `Li, Diego_2/07-31-26/x.pdf`), so the
// path that identified the row before the action does not exist after it. The
// file name survives the move, which is why faxDocumentKey is the identity used
// here and by Rematch and Unmatch.

export const FAX_ROW_ATTR = "data-fax-doc";
export const FLASH_CLASS = "fax-row-flash";
export const FLASH_MS = 1600;

/** The row element for a document key, or null when it is not rendered. */
export function findFaxRow(docKey, root = typeof document !== "undefined" ? document : null) {
  if (!docKey || !root) return null;
  return root.querySelector(`[${FAX_ROW_ATTR}="${CSS.escape(String(docKey))}"]`);
}

/**
 * Centre `docKey`'s row and flash it. -> true when the row was found.
 *
 * Returns false rather than throwing when the row is not in the DOM — it may be
 * inside a collapsed date group, or filtered out of the current view, and the
 * caller decides what to do about that.
 */
export function scrollFaxIntoView(docKey, options = {}) {
  const {
    root = typeof document !== "undefined" ? document : null,
    behavior = "smooth",
    flashMs = FLASH_MS,
  } = options;

  const row = findFaxRow(docKey, root);
  if (!row) return false;

  if (typeof row.scrollIntoView === "function") {
    row.scrollIntoView({ behavior, block: "center", inline: "nearest" });
  }

  // A brief highlight, because a centred row still has to be told apart from
  // its neighbours — the page just re-rendered and everything looks the same.
  try {
    row.classList.add(FLASH_CLASS);
    setTimeout(() => row.classList.remove(FLASH_CLASS), flashMs);
  } catch (e) {
    // Class list unavailable (detached node); the scroll already happened.
  }
  return true;
}

/**
 * Which inbox filters are hiding this fax, if any.
 *
 * Acting on a fax can move it out from under the filter you were using — the
 * usual case being Match while filtered to Unmatched, where the entry you just
 * acted on is precisely the one the filter now excludes. Rather than leaving
 * you staring at a list that no longer contains it, the caller relaxes the
 * filters named here.
 *
 * Only the filters that actually hide it are reported: clearing the report-type
 * filter because the STATUS filter was the problem would throw away a choice
 * that was never in the way.
 *
 * @returns {{status: boolean, type: boolean}}
 */
export function faxFiltersHiding(fax, { faxFilter, reportTypeFilter } = {}) {
  const row = fax || {};
  const status =
    (faxFilter === "unmatched" && !row.is_unmatched) ||
    (faxFilter === "matched" && !!row.is_unmatched);
  const type =
    (reportTypeFilter === "fax" && !row.is_faxed_report) ||
    (reportTypeFilter === "reports" && !!row.is_faxed_report);
  return { status: !!status, type: !!type };
}

/**
 * Split a selection into the faxes to match and the ones to rematch.
 *
 * The bulk button used to be labelled "Rematch" whatever you had selected, and
 * its handler skipped unmatched faxes outright — so selecting every unmatched
 * fax offered an action that refused all of them. An unmatched fax just needs
 * matching; a matched one needs unmatching first. The button should do the
 * right thing for what is actually selected, and say which.
 *
 * Deduplicated by document: one received fax is several inbox rows (the visit
 * copy and the roster copy, in each schema), and acting on it twice reports the
 * second attempt as a failure.
 *
 * @returns {{toMatch: Array, toRematch: Array, label: string}}
 */
export function partitionForBulkMatch(selected, docKeyOf) {
  const seen = new Set();
  const toMatch = [];
  const toRematch = [];

  for (const fax of selected || []) {
    const key = docKeyOf ? docKeyOf(fax) : fax?.file_path;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    (fax?.is_unmatched ? toMatch : toRematch).push(fax);
  }

  const total = toMatch.length + toRematch.length;
  let label = "Match";
  if (toRematch.length && toMatch.length) label = "Match / Rematch";
  else if (toRematch.length) label = "Rematch";
  return { toMatch, toRematch, label: `${label} (${total})` };
}
